import { createPortal } from "react-dom";
import { useEffect, useLayoutEffect, useMemo, useState } from "react";

const getCSSVariablesFromClass = (className) => {
    const styles = {};
    for (const sheet of document.styleSheets) {
        try {
            for (const rule of sheet.cssRules) {
                if (rule.selectorText === className) {
                    for (const property of rule.style) {
                        if (property.startsWith('--')) {
                            styles[property] = rule.style.getPropertyValue(property).trim();
                        }
                    }
                }
            }
        } catch (e) {
            // Ignora errori di accesso a fogli di stile di dominio diverso
            console.warn(e);
        }
    }
    return styles;
};

const PiPWindow = ({ pipWindow, children, className, styles, onOpen, onResize }) => {
    const [mounted, setMounted] = useState(false);

    // Crea un container stabile per il portal dentro la PiP window
    const containerEl = useMemo(() => {
        if (!pipWindow || pipWindow.closed) return null;
        try {
            return pipWindow.document?.createElement('div') ?? null;
        } catch (e) {
            return null;
        }
    }, [pipWindow]);

    // Monta/smonta il container dentro il body della PiP in modo sicuro
    useLayoutEffect(() => {
        if (!pipWindow || pipWindow.closed || !containerEl) return;
        const body = pipWindow.document?.body;
        if (!body) return;

        // Attach a stable container inside the PiP window
        body.appendChild(containerEl);
        setMounted(true);

        return () => {
            // Mark as unmounted so portal stops rendering
            setMounted(false);
            try {
                // Only attempt removal if the container still belongs to this body
                if (containerEl.parentNode === body) {
                    body.removeChild(containerEl);
                }
            } catch (_) {
                // ignore
            }
        };
    }, [pipWindow, containerEl]);

    // Applica CSS vars e listeners (resize, beforeunload)
    useEffect(() => {
        if (!mounted || !pipWindow || pipWindow.closed) return;

        // Applica variabili CSS al :root della PiP
        try {
            const pipRoot = pipWindow.document?.documentElement;
            if (pipRoot) {
                const classStyles = getCSSVariablesFromClass('.starting-finance');
                const combinedStyles = { ...classStyles, ...styles };
                Object.keys(combinedStyles || {}).forEach((property) => {
                    if (property.startsWith('--')) {
                        pipRoot.style.setProperty(property, combinedStyles[property]);
                    }
                });
            }
        } catch (_) {
            // ignora errori di accesso cross-domain
        }

        const handleResize = () => {
            if (!pipWindow || pipWindow.closed) return;
            onResize?.(pipWindow.innerWidth, pipWindow.innerHeight);
        };

        const handleBeforeUnload = () => {
            // PiP window is going away; stop rendering to avoid React removeChild races
            setMounted(false);
        };
        const handlePageHide = () => {
            setMounted(false);
        };

        try {
            pipWindow.addEventListener('resize', handleResize);
            pipWindow.addEventListener('beforeunload', handleBeforeUnload);
            pipWindow.addEventListener('pagehide', handlePageHide);
        } catch (_) {
            // ignore
        }

        try {
            onOpen?.(pipWindow.innerWidth, pipWindow.innerHeight);
        } catch (_) {
            // ignore consumer errors
        }

        return () => {
            try {
                pipWindow.removeEventListener('resize', handleResize);
                pipWindow.removeEventListener('beforeunload', handleBeforeUnload);
                pipWindow.removeEventListener('pagehide', handlePageHide);
            } catch (_) {
                // ignore
            }
        };
    }, [mounted, pipWindow, styles, onResize, onOpen]);

    // Do not attempt to render if the PiP window is already closed
    if (!mounted || !containerEl || (pipWindow && pipWindow.closed)) return null;

    return createPortal(
        <div className={className}>{children}</div>,
        containerEl
    );
};

export default PiPWindow;